import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, formatBRL, formatDateTimeBR } from "../../lib/api";
import { savePending } from "../../lib/booking-storage";
import { hourBankName } from "../../lib/hourbank";
import { spLocalToIso } from "../../lib/datetime";
import { Plus, Calendar, MapPin, Clock, X, Users, ArrowRight, Info, ShoppingBag, Video } from "lucide-react";
import { toast } from "sonner";
import SpaceGallery from "../../components/SpaceGallery";

function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

export default function ClientReservas() {
  const [items, setItems] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [packages, setPackages] = useState([]);
  const [step, setStep] = useState("list"); // list | pick-space | form
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ start_time: "", end_time: "", notes: "", payment_method: "pix" });
  const [block, setBlock] = useState(null);
  const [profile, setProfile] = useState(null);
  const [dados, setDados] = useState(null);
  const [editData, setEditData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastDebit, setLastDebit] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const nav = useNavigate();
  const location = useLocation();

  const profileToDados = (p) => ({
    name: p?.name || "",
    cpf: p?.cpf || "",
    rg: p?.rg || "",
    birth_date: p?.birth_date || "",
    phone: p?.phone || "",
    email: p?.email || "",
    cep: p?.address?.cep || "",
    street: p?.address?.street || "",
    number: p?.address?.number || "",
    complement: p?.address?.complement || "",
    neighborhood: p?.address?.neighborhood || "",
    city: p?.address?.city || "",
    state: p?.address?.state || "",
  });

  const load = () => Promise.all([
    api.get("/reservations").then(r => setItems(r.data)),
    api.get("/spaces").then(r => setSpaces(r.data.filter(s => s.active !== false))),
    api.get("/me/packages").then(r => setPackages(r.data)).catch(() => setPackages([])),
    api.get("/me/profile").then(r => { setProfile(r.data); setDados(profileToDados(r.data)); }).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  // ?categoria=Coworking → abrir já o passo "pick-space" filtrado
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get("categoria");
    if (cat) {
      setCategoryFilter(cat);
      setStep("pick-space");
    }
  }, [location.search]);

  const activePkgCats = new Set(
    packages.filter(p => p.status === "active").map(p => p.plan_name).filter(Boolean)
  );
  // Match by category isn't directly on package object; we just hint by plan_name
  // (visual cue only — backend is the source of truth for compatibility check)

  // RC5 — campos obrigatórios faltantes no perfil (mesma regra do backend RC4)
  const computeMissing = (d) => {
    if (!d) return [];
    const m = [];
    if ((d.cpf || "").replace(/\D/g, "").length !== 11) m.push("cpf");
    if ((d.rg || "").trim().length < 5) m.push("rg");
    if ((d.phone || "").trim().length < 8) m.push("phone");
    if ((d.cep || "").replace(/\D/g, "").length !== 8) m.push("cep");
    if ((d.street || "").trim().length < 2) m.push("street");
    if (!(d.number || "").trim()) m.push("number");
    if ((d.neighborhood || "").trim().length < 2) m.push("neighborhood");
    if ((d.city || "").trim().length < 2) m.push("city");
    if ((d.state || "").trim().length !== 2) m.push("state");
    return m;
  };
  const missingNow = computeMissing(dados);

  // Diff dados editados × perfil carregado → payload do PUT /me/profile
  const profileChanges = () => {
    if (!dados || !profile) return {};
    const orig = profileToDados(profile);
    const map = {
      name: "name", cpf: "cpf", rg: "rg", birth_date: "birth_date", phone: "phone",
      cep: "address_cep", street: "address_street", number: "address_number",
      complement: "address_complement", neighborhood: "address_neighborhood",
      city: "address_city", state: "address_state",
    };
    const out = {};
    for (const [k, apiKey] of Object.entries(map)) {
      const v = (dados[k] || "").trim();
      if (v !== (orig[k] || "").trim() && (v !== "" || k === "complement")) out[apiKey] = v;
    }
    return out;
  };

  const create = async (e) => {
    e.preventDefault();
    setBlock(null);
    setSaving(true);
    try {
      // 1) Perfil = fonte única da verdade: salvar alterações antes de reservar
      const changes = profileChanges();
      if (Object.keys(changes).length > 0) {
        const { data: updated } = await api.put("/me/profile", changes);
        setProfile(updated);
        setDados(profileToDados(updated));
      }
      // 2) Pipeline financeiro único (RC4) — lê o perfil atualizado no backend
      // RC8: o horário digitado é hora de São Paulo, independente do fuso do navegador
      const payload = {
        space_id: selected.id,
        start_time: spLocalToIso(form.start_time),
        end_time: spLocalToIso(form.end_time),
        notes: form.notes || null,
        payment_method: form.payment_method,
      };
      const { data } = await api.post("/portal/reservations", payload);
      if (data.status === "confirmed") {
        if (data.origin === "package") {
          // RC19 — o débito do Banco de Horas ocorre somente no check-out
          const reserved = Number(data.hours_reserved || data.hours_consumed || 0);
          setLastDebit(null);
          toast.success(`Reserva confirmada! ${reserved}h previstas — o débito do Banco de Horas ocorre no check-out, conforme o tempo utilizado.`);
        } else {
          setLastDebit(null);
          toast.success("Reserva confirmada!");
        }
        setStep("list"); setSelected(null); setEditData(false);
        setForm({ start_time: "", end_time: "", notes: "", payment_method: "pix" });
        load();
      } else if (data.status === "pending_payment") {
        savePending({
          reservation_id: data.reservation_id,
          public_token: data.public_token,
          expires_at: data.expires_at,
          space_name: selected.name,
          pix_copy_paste: data.pix_copy_paste,
          pix_qrcode: data.pix_qrcode,
          payment_link: data.payment_link,
          boleto_url: data.boleto_url,
          payment_method: form.payment_method,
        });
        toast.info(form.payment_method === "pix" ? "Pague o PIX para confirmar sua reserva."
          : form.payment_method === "cartao" ? "Finalize o pagamento com cartão para confirmar sua reserva."
          : "Pague o boleto para confirmar sua reserva.");
        nav(`/reservar/pagamento?rid=${data.reservation_id}&token=${data.public_token}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 422 && detail?.code === "profile_incomplete") {
        setEditData(true);
        toast.warning(detail.message || "Complete seus dados cadastrais para reservar.");
      } else if (err.response?.status === 402 && typeof detail === "object" && detail) {
        setBlock(detail);
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Erro ao reservar"));
      }
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (id) => {
    if (!confirm("Cancelar esta reserva?")) return;
    await api.patch(`/reservations/${id}?status=cancelled`);
    toast.success("Reserva cancelada");
    load();
  };

  const goBuyRecommended = () => {
    if (block?.recommended?.plan_id) {
      nav(`/portal/comprar-pacote?plan_id=${block.recommended.plan_id}`);
    } else {
      nav("/portal/comprar-pacote");
    }
  };

  return (
    <div data-testid="client-reservas">
      <div className="admin-page-header">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Minhas Reservas</div>
          <h1 className="font-display text-2xl sm:text-3xl text-[#14213D] font-light">Reservas</h1>
        </div>
        {step === "list" && (
          <button onClick={() => setStep("pick-space")} className="w-full sm:w-auto bg-[#14213D] text-white px-5 py-2.5 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#1F335E]" data-testid="new-reservation-btn">
            <Plus size={16} /> Nova reserva
          </button>
        )}
        {step !== "list" && (
          <button onClick={() => { setStep("list"); setSelected(null); setBlock(null); }} className="w-full sm:w-auto border border-gray-300 text-[#14213D] px-5 py-2.5 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-gray-50">
            ← Voltar
          </button>
        )}
      </div>

      {/* Banner explicativo pacote x categoria */}
      {step !== "list" && (
        <div className="mb-5 rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4 flex items-start gap-3" data-testid="reservation-category-info">
          <Info size={16} className="text-[#7a6519] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#14213D] leading-relaxed">
            <strong>Cada espaço pertence a uma categoria.</strong> Se você tiver um pacote ativo com saldo suficiente,
            suas horas serão debitadas <em>sem cobrança adicional</em>. Caso contrário, geraremos um PIX e a reserva
            só é confirmada após o pagamento.
          </div>
        </div>
      )}

      {/* STEP 1 — Seleção visual de espaço */}
      {step === "pick-space" && (
        <>
          {categoryFilter && (
            <div className="mb-4 text-xs text-gray-500" data-testid="category-filter-hint">
              Mostrando apenas espaços da categoria: <strong className="text-[#14213D]">{categoryFilter}</strong>{" "}
              · <button onClick={() => setCategoryFilter(null)} className="underline text-[#D4AF37] hover:text-[#7a6519]">limpar filtro</button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="space-picker-grid">
            {spaces
              .filter(s => !categoryFilter || (s.category || "").toLowerCase() === categoryFilter.toLowerCase())
              .map((s) => {
            const hasPkg = packages.some(p => p.status === "active" && p.plan_name?.toLowerCase().includes((s.category || "").toLowerCase().split(" ")[0]));
            return (
              <button
                type="button"
                key={s.id}
                onClick={() => { setSelected(s); setStep("form"); }}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-[#D4AF37] hover:shadow-md transition text-left group"
                data-testid={`pick-space-${s.id}`}
              >
                <div className="aspect-video bg-gray-100 overflow-hidden relative">
                  {s.photos?.[0] ? (
                    <img src={resolveUrl(s.photos[0])} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#14213D] to-[#1F335E] flex items-center justify-center text-[#D4AF37] font-display text-3xl">{s.name?.[0]}</div>
                  )}
                  {s.video_url && (
                    <div className="absolute top-2 right-2 bg-[#D4AF37] text-[#14213D] text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1"><Video size={10} /> Tour 360°</div>
                  )}
                  {hasPkg && (
                    <div className="absolute bottom-2 left-2 bg-emerald-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">✓ Coberto pelo seu pacote</div>
                  )}
                </div>
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{s.category || s.type}</div>
                  <div className="font-semibold text-[#14213D] mt-1">{s.name}</div>
                  <div className="text-xs text-gray-500 line-clamp-2 mt-1 mb-3">{s.description}</div>
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-3">
                    <span className="flex items-center gap-1"><Users size={12} /> até {s.capacity}</span>
                    {s.hourly_rate > 0 && <span className="font-semibold">{formatBRL(s.hourly_rate)}/hora</span>}
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {(s.resources || []).slice(0, 3).map((r) => (
                      <span key={r} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{r}</span>
                    ))}
                  </div>
                  <div className="text-xs text-[#14213D] font-semibold inline-flex items-center gap-1">
                    Selecionar <ArrowRight size={12} />
                  </div>
                </div>
              </button>
            );
          })}
          </div>
        </>
      )}

      {/* STEP 2 — Form com galeria e detalhes */}
      {step === "form" && selected && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="reservation-space-preview">
            <SpaceGallery photos={selected.photos || []} videoUrl={selected.video_url} name={selected.name} />
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{selected.category || selected.type}</div>
              <div className="font-display text-2xl text-[#14213D]">{selected.name}</div>
              <div className="text-sm text-gray-600 mt-2">{selected.description}</div>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 mt-3">
                <span className="flex items-center gap-1"><Users size={12} className="text-[#D4AF37]" /> Capacidade: {selected.capacity}</span>
                {selected.hourly_rate > 0 && <span>{formatBRL(selected.hourly_rate)}/hora</span>}
                {selected.daily_rate > 0 && <span>{formatBRL(selected.daily_rate)}/dia</span>}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {(selected.resources || []).map((r) => (
                  <span key={r} className="text-[11px] px-2 py-1 rounded-full bg-gray-100 text-gray-700">{r}</span>
                ))}
              </div>
            </div>
          </div>

          <form onSubmit={create} className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-5 space-y-4 self-start" data-testid="reservation-form">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Dados da reserva</div>
            <div>
              <label className="text-xs text-gray-500">Início *</label>
              <input required type="datetime-local" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="w-full mt-1.5 border border-gray-200 rounded px-3 py-2 text-sm" data-testid="start-time" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Fim *</label>
              <input required type="datetime-local" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="w-full mt-1.5 border border-gray-200 rounded px-3 py-2 text-sm" data-testid="end-time" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Observações</label>
              <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full mt-1.5 border border-gray-200 rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Forma de pagamento (se não coberto pelo Banco de Horas)</label>
              <div className="flex gap-2 mt-1.5" data-testid="portal-payment-methods">
                {[["pix", "PIX"], ["cartao", "Cartão"]].map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setForm({ ...form, payment_method: v })}
                    data-testid={`portal-method-${v}`}
                    className={`flex-1 rounded border py-2 text-xs font-bold transition-all ${
                      form.payment_method === v ? "border-[#14213D] bg-[#14213D] text-white" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                    }`}>{label}</button>
                ))}
              </div>
            </div>
            {form.start_time && form.end_time && (() => {
              const hours = Math.max(0, (new Date(form.end_time) - new Date(form.start_time)) / 3600000);
              const nowIso = new Date().toISOString();
              const best = packages
                .filter(p => p.status === "active" && (p.hours_remaining || 0) >= hours && hours > 0 && (p.valid_until || "") > nowIso)
                .sort((a, b) => (a.valid_until || "").localeCompare(b.valid_until || ""))[0];
              if (best) {
                const after = +((best.hours_remaining || 0) - hours).toFixed(1);
                return (
                  <div className="rounded-lg border-2 border-[#D4AF37]/50 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-4" data-testid="hour-bank-preview">
                    <div className="text-xs uppercase tracking-widest text-[#7a5f10] font-bold mb-1">✦ Você possui um Banco de Horas ativo</div>
                    <div className="text-sm font-bold text-[#14213D] mb-3">{hourBankName(best)}</div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-white rounded p-2">
                        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Saldo atual</div>
                        <div className="text-base font-black text-[#14213D]">{best.hours_remaining}h</div>
                      </div>
                      <div className="bg-white rounded p-2">
                        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Esta reserva</div>
                        <div className="text-base font-black text-amber-600">−{hours}h</div>
                      </div>
                      <div className="bg-white rounded p-2">
                        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Saldo restante</div>
                        <div className="text-base font-black text-emerald-700">{after}h</div>
                      </div>
                    </div>
                    <div className="text-xs text-emerald-700 font-bold mt-2.5">✓ Sem PIX — reserva confirmada automaticamente.</div>
                  </div>
                );
              }
              if (selected.hourly_rate > 0) {
                return (
                  <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded p-3 text-sm">
                    <span className="text-gray-700">Valor avulso estimado: </span>
                    <span className="font-bold text-[#14213D]">{formatBRL(hours * (selected.hourly_rate || 0))}</span>
                    <div className="text-[11px] text-gray-500 mt-1">Pagamento via PIX — a reserva é confirmada após a aprovação.</div>
                  </div>
                );
              }
              return null;
            })()}

            {/* Mensagem de bloqueio 402 amigável */}
            {block && (
              <div className="bg-amber-50 border border-amber-200 rounded p-4 space-y-3" data-testid="reservation-block">
                <div className="font-semibold text-amber-900 flex items-start gap-2">
                  <Info size={16} className="mt-0.5 flex-shrink-0" />
                  <span>Tudo pronto! Você só precisa de um pacote compatível para reservar este espaço.</span>
                </div>
                <div className="text-sm text-amber-800">{block.message}</div>
                {block.recommended && (
                  <div className="rounded bg-white border border-amber-300 p-3 text-sm">
                    <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold">Pacote recomendado</div>
                    <div className="font-display text-base text-[#14213D] mt-1">{block.recommended.plan_name}</div>
                    {block.recommended.price_package > 0 && (
                      <div className="text-xs text-gray-600">{formatBRL(block.recommended.price_package)} · {block.recommended.package_hours}h · 30 dias</div>
                    )}
                  </div>
                )}
                <button type="button" onClick={goBuyRecommended} className="w-full bg-[#D4AF37] hover:bg-[#E8C657] text-[#14213D] font-bold py-2.5 rounded text-sm inline-flex items-center justify-center gap-2" data-testid="buy-recommended-btn">
                  <ShoppingBag size={14} /> Comprar pacote recomendado
                </button>
              </div>
            )}

            {/* RC5 — Seus dados: perfil = fonte única da verdade (autofill + edição) */}
            {dados && (
              <div className="border border-gray-200 rounded p-4 space-y-3" data-testid="client-data-section">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Seus dados</div>
                  <button type="button" onClick={() => setEditData(v => !v)} className="text-xs text-[#7a6519] font-semibold hover:underline" data-testid="edit-data-btn">
                    {editData ? "Ocultar edição" : "Editar dados"}
                  </button>
                </div>

                {missingNow.length === 0 && !editData && (
                  <div className="text-xs text-gray-600 leading-relaxed" data-testid="client-data-summary">
                    <div className="text-emerald-700 font-semibold mb-1">✓ Cadastro completo — usaremos os dados abaixo</div>
                    <div><strong className="text-[#14213D]">{dados.name}</strong> · CPF {dados.cpf} · {dados.phone}</div>
                    <div>{dados.street}, {dados.number}{dados.complement ? ` — ${dados.complement}` : ""} · {dados.neighborhood} · {dados.city}/{dados.state} · CEP {dados.cep}</div>
                  </div>
                )}

                {missingNow.length > 0 && !editData && (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="missing-data-hint">
                    Complete apenas o(s) dado(s) abaixo — salvaremos no seu cadastro e <strong>não pediremos novamente</strong>.
                  </div>
                )}

                {(editData || missingNow.length > 0) && (
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ["name", "Nome completo", "text", "col-span-2"],
                      ["cpf", "CPF", "text", ""],
                      ["rg", "RG", "text", ""],
                      ["birth_date", "Data de nascimento", "date", ""],
                      ["phone", "Telefone/WhatsApp", "text", ""],
                      ["email", "E-mail", "email", "col-span-2"],
                      ["cep", "CEP", "text", ""],
                      ["state", "UF", "text", ""],
                      ["street", "Rua", "text", "col-span-2"],
                      ["number", "Número", "text", ""],
                      ["complement", "Complemento", "text", ""],
                      ["neighborhood", "Bairro", "text", ""],
                      ["city", "Cidade", "text", ""],
                    ]
                      .filter(([key]) => editData || missingNow.includes(key))
                      .map(([key, label, type, span]) => (
                        <div key={key} className={span}>
                          <label className="text-[11px] text-gray-500">{label}{missingNow.includes(key) ? " *" : ""}</label>
                          <input
                            type={type}
                            required={missingNow.includes(key)}
                            disabled={key === "email"}
                            maxLength={key === "state" ? 2 : undefined}
                            value={dados[key] || ""}
                            onChange={(e) => setDados({
                              ...dados,
                              [key]: key === "cpf" || key === "cep" ? e.target.value.replace(/\D/g, "")
                                : key === "state" ? e.target.value.toUpperCase() : e.target.value,
                            })}
                            className="w-full mt-0.5 border border-gray-200 rounded px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                            data-testid={`dados-${key}`}
                          />
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}


            <button type="submit" disabled={saving} className="w-full bg-[#14213D] hover:bg-[#1F335E] disabled:opacity-60 text-white font-semibold py-2.5 rounded text-sm" data-testid="save-reservation">{saving ? "Processando..." : "Confirmar reserva"}</button>
          </form>
        </div>
      )}

      {/* STEP 0 — Lista de reservas */}
      {step === "list" && (
        <div className="space-y-3">
          {/* RC6 — resumo do consumo do Banco de Horas na última reserva */}
          {lastDebit && (
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-4" data-testid="last-debit-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold mb-2">✓ Reserva confirmada com seu Banco de Horas</div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-white rounded p-2">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Saldo antes</div>
                      <div className="text-lg font-black text-[#14213D]" data-testid="debit-before">{lastDebit.before}h</div>
                    </div>
                    <div className="bg-white rounded p-2">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Utilizadas</div>
                      <div className="text-lg font-black text-amber-600" data-testid="debit-used">−{lastDebit.used}h</div>
                    </div>
                    <div className="bg-white rounded p-2">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Saldo atual</div>
                      <div className="text-lg font-black text-emerald-700" data-testid="debit-after">{lastDebit.after}h</div>
                    </div>
                  </div>
                </div>
                <button onClick={() => setLastDebit(null)} className="text-emerald-700 text-xs font-bold hover:underline" data-testid="dismiss-debit-card">Fechar</button>
              </div>
              {/* RC7 — ações pós-reserva */}
              <div className="flex flex-wrap gap-2 mt-3">
                <button onClick={() => { setLastDebit(null); setStep("pick-space"); }} className="text-xs font-bold bg-[#14213D] text-white rounded-lg px-4 py-2 hover:bg-[#1F335E] active:scale-[0.99] transition-all" data-testid="post-booking-new">
                  Nova reserva
                </button>
                <button onClick={() => nav("/portal")} className="text-xs font-bold border-2 border-emerald-300 text-emerald-700 rounded-lg px-4 py-2 hover:bg-emerald-100 active:scale-[0.99] transition-all" data-testid="post-booking-bank">
                  Meu Banco de Horas
                </button>
              </div>
            </div>
          )}
          {items.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400" data-testid="no-reservations">
              <Calendar size={32} className="mx-auto mb-3 text-gray-300" />
              Você ainda não fez nenhuma reserva
            </div>
          ) : items.map((r) => (
            <div key={r.id} className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`reservation-${r.id}`}>
              <div className="flex items-start gap-4 min-w-0">
                <div className="w-12 h-12 rounded-md bg-[#14213D] text-[#D4AF37] flex items-center justify-center flex-shrink-0">
                  <Calendar size={20} />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-[#14213D] flex items-center gap-2"><MapPin size={13} className="text-[#D4AF37]" />{r.space_name}</div>
                  <div className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-2">
                    <span className="flex items-center gap-1"><Clock size={11} /> {formatDateTimeBR(r.start_time)}</span>
                    <span>→</span>
                    <span>{formatDateTimeBR(r.end_time)}</span>
                  </div>
                  <div className="text-sm text-[#14213D] font-semibold mt-1">
                    {r.package_id ? (
                      Number(r.hours_consumed || r.hours_debited || 0) > 0
                        ? <span className="text-emerald-700">{Number(r.hours_consumed || r.hours_debited).toFixed(1)}h debitadas do pacote</span>
                        : <span className="text-emerald-700">{Number(r.hours_reserved || r.duration_hours || r.hours || 0).toFixed(1)}h previstas · débito no check-out</span>
                    ) : formatBRL(r.price)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className={`text-[11px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold ${
                  r.status === "confirmed" ? "bg-emerald-50 text-emerald-700" :
                  r.status === "completed" ? "bg-blue-50 text-blue-700" :
                  r.status === "cancelled" ? "bg-red-50 text-red-700" :
                  r.status === "expired" ? "bg-gray-100 text-gray-500" : "bg-amber-50 text-amber-700"
                }`}>{r.status === "pending_payment" ? "aguardando pagamento" : r.status === "expired" ? "expirada" : r.status}</span>
                {r.status === "pending_payment" && r.public_token && (
                  <button onClick={() => nav(`/reservar/pagamento?rid=${r.id}&token=${r.public_token}`)} className="bg-[#D4AF37] text-[#14213D] text-xs font-bold px-3 py-1.5 rounded hover:bg-[#E8C657]" data-testid={`pay-${r.id}`}>Pagar agora</button>
                )}
                {r.status === "confirmed" && new Date(r.start_time) > new Date() && (
                  <button onClick={() => cancel(r.id)} className="text-red-600 text-xs flex items-center gap-1 hover:underline" data-testid={`cancel-${r.id}`}><X size={12} /> Cancelar</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
